import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import format from 'date-fns/format';

import {
    TextButton,
    Button,
    Checkbox,
    FormField,
    Label,
    LabeledControl,
    Input,
    Spacer,
    Text,
} from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { PhoneVerificationWizard } from 'app/shared/components/Wizard/PhoneVerificationWizard';
import numberUtils from 'app/shared/utils/numberUtils';
import { StyledAnchor } from '../UtilsStyledComponents';
import ContactTextInput from './components/ContactTextInput';
import { ForRentBy } from './components/ForRentBy';
import {
    StyledAdornment,
    StyledAdornedInput,
    StyledAlert,
    StyledFormHelp,
} from './StyledComponents';

const ContactInformationSF = ({
    contactNameRef,
    contactPhoneRef,
    datePhoneVerified,
    errorObj,
    formData,
    forRentByRef,
    immediateUpdateAndSave,
    isInputFormDisabled,
    isPaymentRequired,
    onPhoneNumberAdded,
    onVerificationConfirmation,
    onRequestVerificationCode,
    phoneVerificationConfig,
    savePropertyData,
    setShowPhoneVerificationModal,
    showPhoneVerificationModal,
    updateFormData,
    updateOnFocus,
    verifiedPhoneNumber,
}) => {
    const dispatch = useDispatch();
    const {
        aliasEncoded,
        contactEmail: originalContactEmail,
        contactName: originalContactName,
        forRentBy,
        propertyType,
        trackingPhoneNumber,
    } = formData;
    const isDisabled = isInputFormDisabled || false;

    const [contactName, setContactName] = useState(originalContactName);
    const [contactEmail, setContactEmail] = useState(originalContactEmail);

    // Only show 'Hide Phone' checkbox if:
    // 1) For Rent By field is either 'owner' or 'tenant'
    // 2) The listing is NOT an apartment community
    // 3) Phone Number is verified
    const showHidePhoneCheckbox = forRentBy !== 'agent' &&
                                  propertyType !== PropertyConstants.PROPERTY_TYPES.COMMUNITY &&
                                  verifiedPhoneNumber;

    const renderPhoneInfo = () => {
        const defaultPhoneInfo = `The phone number displayed to renters may differ from the one you provide.
        This allows us to better protect your identity and reduce spam. `;

        const trackingPhoneInfo = `The phone number displayed to renters is
            ${numberUtils.formatPhoneNumberInput(trackingPhoneNumber)}.
            This allows us to better protect your identity and reduce spam. `;

        const content = trackingPhoneNumber ? trackingPhoneInfo : defaultPhoneInfo;

        return (
            <StyledAlert
                appearance="info"
                body={content}
                actionButton={(
                    <TextButton
                        fontType="bodySmall"
                        as="a"
                        target="_blank"
                        // eslint-disable-next-line max-len
                        href="https://help.zillowrentalmanager.com/hc/en-us/articles/360047225453-Why-is-a-different-phone-number-showing-on-my-rental-listing-"
                    >
                        Learn More
                    </TextButton>
                )}
            />
        );
    };

    const hidePhoneNumberFromListing = () => {
        const { hidePhone } = formData;
        const toggledHidePhoneValue = !hidePhone;

        immediateUpdateAndSave(
            'hidePhone', toggledHidePhoneValue,
            { preserveFieldName: true }
        );
    };

    const triggerGA = () => {
        const { hidePhone } = formData;
        const toggledHidePhoneValue = !hidePhone;
        const gaEvent = toggledHidePhoneValue ? 'HIDE_PHONE' : 'SHOW_PHONE';

        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS[gaEvent],
            { label: aliasEncoded }
        ));
    };

    const handlePhoneCheckboxChange = () => {
        hidePhoneNumberFromListing();
        triggerGA();
    };

    return (
        <div className="ContactInformation">
            <Text
                marginBottom="xs"
                mediaQuery={{ lg: { marginBottom: 'sm' } }}
                fontType="h4"
                as="div"
            >
                Your contact information
            </Text>
            <Row>
                <Col xs={12} md={12} lg={6}>
                    <StyledAnchor id="contact" />
                    <div ref={contactNameRef} />
                    <ContactTextInput
                        errorObj={errorObj}
                        fieldKey="contactName"
                        isDisabled={isDisabled}
                        isRequired={true}
                        labelName="Name"
                        onChange={setContactName}
                        updateOnFocus={updateOnFocus}
                        savePropertyData={savePropertyData}
                        updateFormData={updateFormData}
                        value={contactName}
                    />
                    <ContactTextInput
                        errorObj={errorObj}
                        fieldKey="contactEmail"
                        isDisabled={isDisabled}
                        isRequired={true}
                        labelName="Email"
                        onChange={setContactEmail}
                        updateFormData={updateFormData}
                        updateOnFocus={updateOnFocus}
                        savePropertyData={savePropertyData}
                        value={contactEmail}
                    />
                    <div ref={contactPhoneRef} />
                    <FormField>
                        <Label required={true}>Phone</Label>
                        {verifiedPhoneNumber ? (
                            <div>
                                <StyledAdornedInput
                                    disabled={true}
                                    input={(
                                        <Input
                                            aria-label="Contact phone"
                                            name="contactPhone"
                                            type="tel"
                                            value={numberUtils.formatPhoneNumberInput(verifiedPhoneNumber)}
                                        />
                                    )}
                                    rightAdornment={(
                                        <StyledAdornment>
                                            <Button
                                                size="sm"
                                                onClick={() => setShowPhoneVerificationModal(true)}
                                            >
                                                Edit
                                            </Button>
                                        </StyledAdornment>
                                    )}
                                />
                                {datePhoneVerified && (
                                    <StyledFormHelp>
                                        {`Verified on ${format(new Date(datePhoneVerified), 'MMMM D, YYYY')}`}
                                    </StyledFormHelp>
                                )}
                            </div>
                        ) : (
                            <Button
                                size="sm"
                                onClick={() => setShowPhoneVerificationModal(true)}
                            >
                                Add phone number
                            </Button>
                        )}
                    </FormField>
                    {showPhoneVerificationModal && (
                        <PhoneVerificationWizard
                            headerText="Add and verify phone number"
                            onPhoneNumberAdded={onPhoneNumberAdded}
                            onRequestVerificationCode={onRequestVerificationCode}
                            onVerificationConfirmation={onVerificationConfirmation}
                            showVerificationModal={setShowPhoneVerificationModal}
                            stepsConfig={phoneVerificationConfig}
                        />
                    )}
                    <Spacer marginTop="xs" />
                    {isPaymentRequired && renderPhoneInfo()}
                    {showHidePhoneCheckbox && (
                        <LabeledControl
                            controlId="LabeledControl-checkbox-hide-phone"
                            data-test="checkbox-hide-phone"
                            label={<Label>Allow renters to contact you by phone</Label>}
                            control={(
                                <Checkbox
                                    name="LabeledControl-checkbox-hide-phone"
                                    checked={!formData.hidePhone}
                                    onChange={handlePhoneCheckboxChange}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                    )}
                    <div ref={forRentByRef} />
                    <Spacer marginTop="sm" marginBottom="lg">
                        <ForRentBy
                            updateFormData={updateFormData}
                            formData={formData}
                            immediateUpdateAndSave={immediateUpdateAndSave}
                            errorObj={errorObj}
                        />
                    </Spacer>
                </Col>
            </Row>
        </div>
    );
};

export default ContactInformationSF;
